import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Box, Card, CardActionArea, CardContent, CardHeader, Grid, List, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import ProgressBar from "../../../Common/ProgressBar/ProgressBar";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import MSFAvatar from "../MSFAvatar";
import ThisTheme from "../../../../themes/MSFTheme";
import RenderDate from "../../../Common/Date/RenderDate";

import msfActions from "../../../../actions/msfActions";
import { getSurveys } from "../../../../reducers";


const getOpenSurvey = (surveys) => {
    if (!surveys?.length) { return null; }
    const openSurveys = surveys.filter(({status}) => status === 'open');
    if (openSurveys.length !== 1) { return null; }

    return openSurveys[0];
};

const SurveyCard = () => {
    const dispatch = useDispatch();

    useEffect(() => dispatch(msfActions.getSurveys("P-MSF")), [dispatch]);

    const surveys = useSelector(getSurveys);
    const openSurvey = getOpenSurvey(surveys);
    let content, subheader;

    if (!surveys?.length) {
        content = <AlertMessage severity="info" message="no surveys found" />;
        subheader = (
            <Typography component="span" className="card-header-subtitle">
                0 surveys
            </Typography>
        );
    } else if (!!openSurvey) {
        const result = (!openSurvey || openSurvey.result.returned === 0) ? 0 : openSurvey.result.returned / openSurvey.result.required;
        content = (
            <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={6}>
                    <Box className="card-content-progress-circle">
                        <CircularProgressbar
                            value={result}
                            maxValue={1}
                            text={`${Math.floor(result * 100)}%`}
                            strokeWidth={13}
                            styles={{
                                path: {
                                    stroke: ThisTheme.palette.primary.main,
                                    strokeLinecap: "butt",
                                    transition: "stroke-dashoffset 0.5s ease 0s",
                                    transform: "rotate(0.25turn)",
                                    transformOrigin: "center center",
                                },
                                trail: {
                                    stroke: "#EEEFEF",
                                    strokeLinecap: "butt",
                                },
                                text: {
                                    fill: "rgb(117, 117, 117)",
                                    fontSize: "14px",
                                },
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                    <Box className="card-content-progress-data">
                        <p>
                            <em>{openSurvey.result.returned}</em>
                            <br />
                            responses
                        </p>
                    </Box>
                </Grid>
                <Grid item md={12}>
                    <Grid container spacing={2}>
                        <Grid item md={12}>
                            <ProgressBar
                                data={{
                                    title: "Responses",
                                    label: `/${openSurvey.result.required}`,
                                    total: openSurvey.result.returned,
                                    percentage: result * 100,
                                }}
                                theme={ThisTheme}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
        subheader = (
            <Typography component="span" className="card-header-subtitle">
                {!!openSurvey?.startDate && <RenderDate date={openSurvey.startDate} />}
                &nbsp;-&nbsp;
                {!!openSurvey?.endDate && <RenderDate date={openSurvey.endDate} />}
            </Typography>
        );
    } else {
        content = (
            <List sx={{ width: "100%", height: "350px", overflow: "auto" }}>
                {surveys.map(({ uuid, type, startDate, endDate }) => (
                    <ListItemButton
                        alignItems="flex-start"
                        key={uuid}
                        component="div"
                    >
                        <Link to={`/msf/surveys/${uuid}`}>
                            <ListItemText
                                disableTypography
                                primary={
                                    <Typography component="h3" className="list-item-primary" >
                                        {type.name}
                                    </Typography>
                                }
                                secondary={
                                    <Typography component="span">
                                        {!!startDate && (
                                            <RenderDate date={startDate} />
                                        )}
                                        &nbsp;-&nbsp;
                                        {!!endDate && (
                                            <RenderDate date={endDate} />
                                        )}
                                    </Typography>
                                }
                            />
                        </Link>
                    </ListItemButton>
                ))}
            </List>
        );
        subheader = (
            <Typography component="span" className="card-header-subtitle">
                {`${surveys.length} surveys`}
            </Typography>
        );
    }

    return (
        <Card className="card">
            <CardActionArea component={Link} to="/msf/surveys/" centerRipple>
                <CardHeader
                    disableTypography
                    avatar={
                        <Tooltip title="View all surveys" arrow>
                            <MSFAvatar type="survey" />
                        </Tooltip>
                    }
                    title={
                        <Typography component="h2" className="card-header-title" >
                            Surveys
                        </Typography>
                    }
                    subheader={subheader}
                />
            </CardActionArea>
            <CardContent>
                <Box className="card-content">{content}</Box>
            </CardContent>
        </Card>
    );
};

export default SurveyCard;
